import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Badge,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add,
  Remove,
  ShoppingCart,
  ArrowBack,
  Restaurant,
  TableRestaurant,
  CheckCircle,
  Close,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { menuService } from '../../services/menuService';
import { tableService } from '../../services/tableService';
import { orderService } from '../../services/orderService';
import { buildImageUrl } from '../../utils/imageUtils';
import TableOrderLayout from '../../components/user/TableOrderLayout';
import { useNotification } from '../../contexts/NotificationContext';

const TableMenuPage = () => {
  const { uniqueString } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [outletData, setOutletData] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('counter'); // 'counter' or 'online'
  const [specialInstructions, setSpecialInstructions] = useState('');

  // Get table info from URL params
  const tableNumber = searchParams.get('tableNumber') || 'Unknown';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch table data
        const tableResponse = await tableService.getTableOrderPage(uniqueString);
        setOutletData(tableResponse.outlet);
        setTableData(tableResponse.table);
        
        // Fetch menu data
        const [categoriesResponse, menuResponse] = await Promise.all([
          menuService.getPublicCategories(tableResponse.outlet.id),
          menuService.getPublicMenuItems()
        ]);
        
        setCategories(categoriesResponse.data);
        setMenuItems(menuResponse.data);
        
        if (categoriesResponse.data.length > 0) {
          setSelectedCategory(categoriesResponse.data[0].id);
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load menu data');
      } finally {
        setLoading(false);
      }
    };

    if (uniqueString) {
      fetchData();
    }
  }, [uniqueString]);

  const handleAddToCart = (item) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      } else {
        return [...prevCart, { ...item, quantity: 1 }];
      }
    });
    showNotification('Item added to cart', 'success');
  };

  const handleRemoveFromCart = (itemId) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => cartItem.id === itemId);
      if (existingItem && existingItem.quantity > 1) {
        return prevCart.map(cartItem =>
          cartItem.id === itemId
            ? { ...cartItem, quantity: cartItem.quantity - 1 }
            : cartItem
        );
      } else {
        return prevCart.filter(cartItem => cartItem.id !== itemId);
      }
    });
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      showNotification('Your cart is empty', 'error');
      return;
    }

    if (!customerName.trim()) {
      showNotification('Please enter your name', 'error');
      return;
    }

    try {
      const orderData = {
        table_id: tableData.id,
        outlet_id: outletData.id,
        table_number: tableData.table_number,
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim() || null,
        customer_email: null,
        items: cart.map(item => ({
          menu_item_id: item.id,
          quantity: item.quantity,
          size_name: null,
          size_price: 0,
          base_price: item.price,
          total_price: item.price * item.quantity,
          special_instructions: item.specialInstructions || null,
          addons: []
        })),
        subtotal: getTotalPrice(),
        tax_amount: 0,
        discount_amount: 0,
        total_amount: getTotalPrice(),
        special_instructions: specialInstructions.trim() || null,
        payment_method: paymentMethod,
        status: paymentMethod === 'counter' ? 'pending' : 'pending'
      };

      console.log('Placing table order:', orderData);
      
      const response = await orderService.createTableOrder(orderData);
      console.log('Order created successfully:', response);
      
      showNotification('Order placed successfully! Your order number is ' + response.order_number, 'success');
      setCart([]);
      setOrderDialogOpen(false);
      setCustomerName('');
      setCustomerPhone('');
      setSpecialInstructions('');
      setPaymentMethod('counter');
      
      // Navigate back to table page
      navigate(`/table-order/${uniqueString}`);
      
    } catch (err) {
      console.error('Error placing order:', err);
      showNotification('Failed to place order: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const filteredMenuItems = selectedCategory 
    ? menuItems.filter(item => item.category_id === selectedCategory)
    : menuItems;

  if (loading) {
    return (
      <TableOrderLayout title="Loading Menu..." subtitle="Please wait...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </TableOrderLayout>
    );
  }

  if (error) {
    return (
      <TableOrderLayout title="Error" subtitle="Failed to load menu">
        <Container maxWidth="md">
          <Alert severity="error" sx={{ textAlign: 'center', py: 4 }}>
            {error}
          </Alert>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/table-order/${uniqueString}`)}
            >
              Back to Table
            </Button>
          </Box>
        </Container>
      </TableOrderLayout>
    );
  }

  return (
    <TableOrderLayout 
      title={`Table ${tableNumber} Menu`} 
      subtitle={outletData?.name || 'Restaurant Menu'}
      showCart={true}
      cartItemCount={cart.length}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1.5,
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 2
              }}>
                <TableRestaurant />
                <Typography variant="h6" fontWeight="600">
                  Table {tableNumber}
                </Typography>
              </Box>
              <Chip
                icon={<Restaurant />}
                label={outletData?.name || 'Restaurant'}
                color="primary"
                variant="outlined"
              />
            </Box>
            
            <Button
              variant="contained"
              startIcon={<ShoppingCart />}
              onClick={() => setCartOpen(true)}
              sx={{ 
                minWidth: 120,
                position: 'relative'
              }}
            >
              <Badge badgeContent={getTotalItems()} color="error">
                Cart
              </Badge>
            </Button>
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Categories Sidebar */}
          <Grid item xs={12} md={3}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Categories
              </Typography>
              <List dense>
                <ListItem
                  button
                  selected={selectedCategory === null}
                  onClick={() => setSelectedCategory(null)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText primary="All Items" />
                </ListItem>
                {categories.map((category) => (
                  <ListItem
                    key={category.id}
                    button
                    selected={selectedCategory === category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText primary={category.name} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Menu Items */}
          <Grid item xs={12} md={9}>
            <Typography variant="h5" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
              {selectedCategory 
                ? categories.find(cat => cat.id === selectedCategory)?.name || 'Menu Items'
                : 'All Menu Items'
              }
            </Typography>
            
            <Grid container spacing={2}>
              {filteredMenuItems.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                  >
                    {item.image_url && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={buildImageUrl(item.image_url)}
                        alt={item.name}
                        sx={{ objectFit: 'cover' }}
                      />
                    )}
                    
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Typography variant="h6" fontWeight="600" gutterBottom>
                        {item.name}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {item.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" color="primary" fontWeight="600">
                          ₹{item.price}
                        </Typography>
                        
                        <Chip
                          label={item.is_available ? 'Available' : 'Unavailable'}
                          color={item.is_available ? 'success' : 'error'}
                          size="small"
                        />
                      </Box>
                      
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<Add />}
                        onClick={() => handleAddToCart(item)}
                        disabled={!item.is_available}
                        sx={{ 
                          borderRadius: 2,
                          py: 1,
                          fontWeight: 600,
                          fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                          '& .MuiButton-startIcon': {
                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' }
                          }
                        }}
                      >
                        Add to Cart
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {/* Cart Drawer */}
        <Drawer
          anchor="right"
          open={cartOpen}
          onClose={() => setCartOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: { xs: '100%', sm: 400 },
              p: 2
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="600">
              Your Order
            </Typography>
            <IconButton onClick={() => setCartOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          {cart.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Your cart is empty
              </Typography>
            </Box>
          ) : (
            <>
              <List>
                {cart.map((item) => (
                  <ListItem key={item.id} sx={{ px: 0 }}>
                    <ListItemText
                      primary={item.name}
                      secondary={`₹${item.price} each`}
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFromCart(item.id)}
                        >
                          <Remove />
                        </IconButton>
                        <Typography variant="body1" sx={{ minWidth: 20, textAlign: 'center' }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleAddToCart(item)}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6">Total Items:</Typography>
                  <Typography variant="h6">{getTotalItems()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight="600">Total Amount:</Typography>
                  <Typography variant="h6" fontWeight="600" color="primary">
                    ₹{getTotalPrice()}
                  </Typography>
                </Box>
              </Box>
              
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => setOrderDialogOpen(true)}
                sx={{ 
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600
                }}
              >
                Place Order
              </Button>
            </>
          )}
        </Drawer>

        {/* Order Dialog */}
        <Dialog 
          open={orderDialogOpen} 
          onClose={() => setOrderDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight="600">
              Complete Your Order
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Your Name *"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Phone Number (Optional)"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                margin="normal"
              />
              
              <TextField
                fullWidth
                label="Special Instructions (Optional)"
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                margin="normal"
                multiline
                rows={3}
                placeholder="Any special requests for your order..."
              />
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Payment Method
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button
                  variant={paymentMethod === 'counter' ? 'contained' : 'outlined'}
                  onClick={() => setPaymentMethod('counter')}
                  startIcon={<Restaurant />}
                  sx={{ flex: 1 }}
                >
                  Pay at Counter
                </Button>
                <Button
                  variant={paymentMethod === 'online' ? 'contained' : 'outlined'}
                  onClick={() => setPaymentMethod('online')}
                  startIcon={<ShoppingCart />}
                  sx={{ flex: 1 }}
                >
                  Pay Online
                </Button>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              {cart.map((item) => (
                <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    {item.name} x {item.quantity}
                  </Typography>
                  <Typography variant="body2">
                    ₹{item.price * item.quantity}
                  </Typography>
                </Box>
              ))}
              
              <Divider sx={{ my: 1 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="600">Total:</Typography>
                <Typography variant="h6" fontWeight="600" color="primary">
                  ₹{getTotalPrice()}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOrderDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handlePlaceOrder}
              startIcon={<CheckCircle />}
            >
              Place Order
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </TableOrderLayout>
  );
};

export default TableMenuPage;